import React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@mui/material';
import { styled } from '@mui/system';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { IconButton, Typography } from '../../components/atoms';
import Tooltip from '@mui/material/Tooltip';
import { useAppSelector } from '../../common/hooks';

const ActionsContainer = styled(Box)`
    display: flex;
    gap: 12px;
`;

const ApproveButton = styled(IconButton)`
    min-width: 28px;
    min-height: 28px;
    border-radius: 8px;
    border: 1px solid #27ae60;
    color: #27ae60;
    background: #fff;
    &:hover {
        background: #eafaf1;
        color: #27ae60;
        border-color: #219150;
    }
    &.Mui-disabled,
    &[disabled] {
        opacity: 1;
        background: #f3f4f6;
        color: #b0b0b0;
        border-color: #e0e0e0;
        box-shadow: none;
        cursor: not-allowed;
        pointer-events: none;
    }
    &.Mui-disabled svg,
    &[disabled] svg {
        color: #b0b0b0;
    }
`;

const RejectButton = styled(IconButton)`
    min-width: 28px;
    min-height: 28px;
    border-radius: 8px;
    border: 1px solid #eb5757;
    color: #eb5757;
    background: #fff;
    &:hover {
        background: #faeaea;
        color: #eb5757;
        border-color: #c0392b;
    }
`;

interface ApprovalSummaryProps {
    date: string;
    fromUser: string;
    fromUserEmail: string;
    fromUserName: string;
    toUser: string;
    toUserEmail: string;
    toUserName: string;
    submitter: string;
    userId: string;
    onApprove: () => void;
    onReject: () => void;
    // Add your props here
}

const ApprovalSummary: React.FC<ApprovalSummaryProps> = ({
    date,
    fromUserEmail,
    fromUserName,
    toUserEmail,
    toUserName,
    submitter,
    userId,
    onApprove,
    onReject
}) => {
    const { t } = useTranslation();
    const formattedDate = moment(date).format('MMM D, YYYY [at] h:mm A');
    const loggedInUserId = useAppSelector((state) => state?.auth?.user?.userId);
    const checkIfUploaderIsSameAsApprover = userId === loggedInUserId;

    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}
        >
            <Box>
                <Typography variant="body2" sx={{ color: 'black', fontWeight: 500 }}>
                    {formattedDate}{' '}
                    <span style={{ color: '#888' }}>
                        {t('LABELS_CHECK_MAKER_BY')} {submitter}
                    </span>
                </Typography>
                <Typography variant="subtitle1" sx={{ color: 'grey', fontWeight: 500 }}>
                    {'Transfer from: '}
                    {fromUserName} {`(${fromUserEmail})`}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: 'grey', fontWeight: 500 }}>
                    {'Transfer to: '}
                    {toUserName} {`(${toUserEmail})`}
                </Typography>
            </Box>
            <ActionsContainer>
                <Tooltip
                    title={
                        checkIfUploaderIsSameAsApprover
                            ? t('LABELS_CHECK_MAKER_SAME_USER')
                            : t('LABELS_CHECK_MAKER_APPROVE')
                    }
                    placement="top"
                >
                    <span>
                        <ApproveButton
                            iconName={<CheckIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onApprove();
                            }}
                            aria-label={
                                checkIfUploaderIsSameAsApprover
                                    ? t('LABELS_CHECK_MAKER_SAME_USER')
                                    : t('LABELS_CHECK_MAKER_APPROVE')
                            }
                            size="small"
                            disabled={checkIfUploaderIsSameAsApprover}
                        />
                    </span>
                </Tooltip>
                <Tooltip title={t('LABELS_CHECK_MAKER_REJECT')} placement="top">
                    <span>
                        <RejectButton
                            iconName={<CloseIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onReject();
                            }}
                            aria-label={t('LABELS_CHECK_MAKER_REJECT')}
                            size="small"
                        />
                    </span>
                </Tooltip>
            </ActionsContainer>
        </Box>
    );
};

export default ApprovalSummary;
